import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  DollarSign, 
  BookOpen, 
  Users, 
  BarChart3, 
  HardDrive, 
  Settings, 
  UserPlus, 
  Palette, 
  Package, 
  Shield, 
  Globe, 
  Award, 
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Flag,
  FileText
} from 'lucide-react';

interface DashboardSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isPro: boolean;
  hasAccess?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  proOnly?: boolean;
  subItems?: NavItem[];
}

export default function DashboardSidebar({ 
  activeSection, 
  onSectionChange, 
  isPro,
  hasAccess = true
}: DashboardSidebarProps) {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'ภาพรวม',
      icon: Home,
    },
    {
      id: 'revenue',
      label: 'รายได้',
      icon: DollarSign,
    },
    {
      id: 'courses',
      label: 'คอร์ส',
      icon: BookOpen,
    },
    {
      id: 'students',
      label: 'นักเรียน',
      icon: Users,
    },
    {
      id: 'storage',
      label: 'พื้นที่จัดเก็บ',
      icon: HardDrive,
      proOnly: true,
    },
    {
      id: 'quick-actions',
      label: 'การดำเนินการ',
      icon: Settings,
      subItems: [
        {
          id: 'analytics',
          label: 'Analytics',
          icon: TrendingUp,
        },
        {
          id: 'video-reports',
          label: 'รายงานปัญหาวิดีโอ',
          icon: Flag,
        },
        {
          id: 'moderator-management',
          label: 'จัดการผู้ดูแล (Moderator)',
          icon: Shield,
          proOnly: true,
        },
        {
          id: 'approve-students',
          label: 'อนุมัตินักเรียน',
          icon: BookOpen,
        },
        {
          id: 'student-reports',
          label: 'รายงานนักเรียน',
          icon: Users,
        },
        {
          id: 'discounts',
          label: 'จัดการส่วนลด',
          icon: BarChart3,
        },
      ],
    },
    {
      id: 'pro-features',
      label: 'ฟีเจอร์พิเศษ',
      icon: Award,
      proOnly: true,
      subItems: [
        {
          id: 'team-management',
          label: 'จัดการทีม',
          icon: UserPlus,
          proOnly: true,
        },
        {
          id: 'white-label',
          label: 'White Label',
          icon: Palette,
          proOnly: true,
        },
        {
          id: 'storage-addons',
          label: 'Add-ons (นักเรียน+พื้นที่)',
          icon: Package,
          proOnly: true,
        },
        {
          id: 'custom-domain',
          label: 'Custom Domain',
          icon: Globe,
          proOnly: true,
        },
        {
          id: 'sla-support',
          label: 'SLA Support',
          icon: Shield,
          proOnly: true,
        },
      ],
    },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (item.proOnly && !isPro) return false;
    return true;
  }).map(item => {
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.filter(subItem => {
          if (subItem.proOnly && !isPro) return false;
          return true;
        })
      };
    }
    return item;
  });

  const renderNavItem = (item: NavItem, level = 0) => {
    const isActive = activeSection === item.id || !!item.subItems?.some(sub => sub.id === activeSection);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isDisabled = !hasAccess && item.id !== 'overview';

    return (
      <div key={item.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <button
          onClick={() => {
            if (isDisabled) return;
            if (hasSubItems) {
              toggleExpanded(item.id);
            } else {
              onSectionChange(item.id);
            }
          }}
          disabled={isDisabled}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
            isDisabled
              ? 'cursor-not-allowed opacity-50 bg-gray-50 text-gray-400'
              : isActive
              ? 'bg-blue-100 text-blue-700 font-semibold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center gap-3">
            <item.icon className={`w-5 h-5 ${
              isDisabled
                ? 'text-gray-400'
                : isActive 
                ? 'text-blue-600'
                : 'text-gray-500'
            }`} />
            <span className="text-sm font-medium">{item.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {item.badge && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600">
                {item.badge}
              </span>
            )}
            {hasSubItems && (
              isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )
            )}
          </div>
        </button>
        
        {hasSubItems && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.subItems!.map(subItem => renderNavItem(subItem, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4">
        <h2 className="text-lg font-bold text-gray-900 mb-6">Dashboard</h2>
        <nav className="space-y-1">
          {filteredNavItems.map(item => renderNavItem(item))}
        </nav>
      </div>
    </div>
  );
}
